import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const translations = {
  en: {
    title: "Define Your Career Preferences",
    subtitle: "These details help us personalize your job-fit score, career path suggestions, and predictive analysis.",
    desiredRole: "Desired Role or Career Path",
    desiredRolePlaceholder: "e.g. UX Designer, Data Analyst",
    noResults: "No results available",
    workStyleTitle: "Work Style Preferences",
    workStyleSubtitle: "Select all that apply",
    selected: "selected",
    keyMotivators: "Key Motivators",
    preferredWorkType: "Preferred Work Type",
    willingToRelocate: "Willing to Relocate?",
    back: "Back",
    next: "Next",
    remote: "Remote",
    hybrid: "Hybrid",
    onsite: "Onsite",
    yes: "Yes",
    no: "No",
    maybe: "Maybe"
  },
  nl: {
    title: "Definieer Uw Carrièrevoorkeuren",
    subtitle: "Deze details helpen ons uw job-fit score, carrièrepad suggesties en voorspellende analyse te personaliseren.",
    desiredRole: "Gewenste Rol of Carrièrepad",
    desiredRolePlaceholder: "bijv. UX Designer, Data Analyst",
    noResults: "Geen resultaten beschikbaar",
    workStyleTitle: "Werkstijlvoorkeuren",
    workStyleSubtitle: "Selecteer alles wat van toepassing is",
    selected: "geselecteerd",
    keyMotivators: "Belangrijkste Motivators",
    preferredWorkType: "Voorkeurs Werktype",
    willingToRelocate: "Bereid om te verhuizen?",
    back: "Terug",
    next: "Volgende",
    remote: "Op afstand",
    hybrid: "Hybride",
    onsite: "Op locatie",
    yes: "Ja",
    no: "Nee",
    maybe: "Misschien"
  }
};

const workStyles = {
  en: [
    "Autonomy",
    "Structure",
    "Teamwork",
    "Fast-paced",
    "Creative",
    "Analytical",
  ],
  nl: [
    "Autonomie",
    "Structuur",
    "Teamwerk",
    "Snel tempo",
    "Creatief",
    "Analytisch",
  ]
};

const motivators = {
  en: [
    "Achievement",
    "Impact",
    "Recognition",
    "Stability",
    "Learning",
    "Purpose",
  ],
  nl: [
    "Prestatie",
    "Impact",
    "Erkenning",
    "Stabiliteit",
    "Leren",
    "Doelgerichtheid",
  ]
};

export default function CareerPreferencesStep({ onNext, setActiveIndicator, preferencesSummary, setPreferencesSummary, isCareerFormValid, setIsCareerFormValid, isDutch, setCareerPreferences }) {
  const navigate = useNavigate();
  const t = translations[isDutch ? 'nl' : 'en'];
  const currentWorkStyles = workStyles[isDutch ? 'nl' : 'en'];
  const currentMotivators = motivators[isDutch ? 'nl' : 'en'];

  const [desiredRole, setDesiredRole] = useState("");
  const [selectedWorkStyles, setSelectedWorkStyles] = useState([]);
  const [selectedMotivators, setSelectedMotivators] = useState([]);
  const [workType, setWorkType] = useState("Remote");
  const [relocation, setRelocation] = useState("Yes");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const toggleSelection = (item, selectedList, setList) => {
    if (selectedList.includes(item)) {
      setList(selectedList.filter((i) => i !== item));
    } else {
      setList([...selectedList, item]);
    }
  };

  const [jobRoles, setJobRoles] = useState([]);
  
  useEffect(() => {
    const fetchJobRoles = async () => {
      if (!desiredRole) return; // Don't fetch if there's no search term

      try {
        const response = await fetch(`https://ec.europa.eu/esco/api/search?language=en&text=${desiredRole}&classId=http://data.europa.eu/esco/model#Occupation`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setJobRoles(data._embedded?.results || []);
      } catch (error) {
        console.error("Error fetching job roles:", error);
      }
    };

    fetchJobRoles();
  }, [desiredRole]); 

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const summary = `Looking for a ${desiredRole || "role"} position that is ${workType.toLowerCase()}. 
    Prefer a work environment that emphasizes ${selectedWorkStyles.length > 0 ? selectedWorkStyles.join(", ") : "flexible work styles"}. 
    Motivated by ${selectedMotivators.length > 0 ? selectedMotivators.join(", ") : "various factors"} and 
    ${relocation === "Yes" && "am willing to relocate" || 
      relocation === "Maybe" && "might be willing to relocate" || 
      "prefer not to relocate"}.`;

    setPreferencesSummary(summary);
  }, [desiredRole, workType, selectedWorkStyles, selectedMotivators, relocation]);

  useEffect(() => {
    const isValid = 
      desiredRole.trim() !== "" && 
      selectedWorkStyles.length > 0 && 
      selectedMotivators.length > 0 && 
      workType && 
      relocation;
      
    setIsCareerFormValid(isValid);
  }, [desiredRole, selectedWorkStyles, selectedMotivators, workType, relocation]);

  useEffect(() => {
    setCareerPreferences({
      desiredRole,
      selectedWorkStyles,
      selectedMotivators,
      workType,
      relocation
    });
  }, [desiredRole, selectedWorkStyles, selectedMotivators, workType, relocation]);

  const HandleBack = () => {
    setActiveIndicator(2);
  };

  
  const handleSubmit = async () => {
    setActiveIndicator(4);
  };

  return (
      <div>
      <div className='mb-4'>
      <h1 className="text-5xl font-bold mb-4 text-primary">{t.title}</h1>
      <p>{t.subtitle}</p>
      </div>

      <div className="border rounded-md p-6">
     
      <div className="relative" ref={dropdownRef}>
      <label className="text-left mb-2 text-xl font-bold">{t.desiredRole}</label>
      <input
        type="text"
        value={desiredRole}
        onChange={(e) => {
          setDesiredRole(e.target.value);
          setShowDropdown(true);
        }}
        className="w-full p-2 border-bottom mb-2"
        placeholder={t.desiredRolePlaceholder}
      />

      {showDropdown && desiredRole !== "" && (
       <div className="absolute z-10 border rounded-md bg-white shadow-md w-full">
        {jobRoles.filter((job) => job.className === "Occupation").length > 0 ? (
          jobRoles
            .filter((job) => job.className === "Occupation").slice(0,6)
            .map((job) => {
              return <p key={job.code} className="search-item p-2"
              onClick={() => {
                setDesiredRole(job.title);
                setShowDropdown(false);
              }}
              >{job.title || "No title available"}</p>;
            })
        ) : (
          <p className="p-1">{t.noResults}</p>
        )}
      </div>
      )}
      </div>

        <div className="mb-6 mt-6">
          <h3 className="text-left text-xl font-bold">
            {t.workStyleTitle} ({selectedWorkStyles.length} {t.selected})
          </h3>
          <p className="text-sm mb-4 text-left opacity-75">{t.workStyleSubtitle}</p>
          <div className="flex gap-2 flex-wrap">
            {currentWorkStyles.map((style) => (
              <div
                key={style}
                className={`button flex items-center justify-center px-6 py-2 rounded-full border cursor-pointer 
                  ${selectedWorkStyles.includes(style) ? "bg-buttonColor text-white" : "bg-white text-gray-700 border-gray-300"}`}
                onClick={() => toggleSelection(style, selectedWorkStyles, setSelectedWorkStyles)}
              >
                {style}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-left text-xl font-bold">
            {t.keyMotivators} ({selectedMotivators.length} {t.selected})
          </h3>
          <p className="text-sm mb-4 text-left opacity-75">{t.workStyleSubtitle}</p>
          <div className="flex gap-2 flex-wrap">
            {currentMotivators.map((motive) => (
              <div
                key={motive}
                className={`button flex items-center justify-center px-6 py-2 rounded-full border cursor-pointer 
                  ${selectedMotivators.includes(motive) ? "bg-buttonColor text-white" : "bg-white text-gray-700 border-gray-300"}`}
                onClick={() => toggleSelection(motive, selectedMotivators, setSelectedMotivators)}
              >
                {motive}
              </div>
            ))}
          </div>
        </div>

      <div className="mb-6">
        <label className="text-left mb-2 text-xl font-bold">{t.preferredWorkType}</label>
        <div className="flex space-x-2">
          {[
            { value: "Remote", label: t.remote },
            { value: "Hybrid", label: t.hybrid },
            { value: "Onsite", label: t.onsite }
          ].map((type) => (
            <button
              key={type.value}
              className={`px-6 py-2 rounded-full border ${
                workType === type.value
                  ? "bg-buttonColor text-white "
                  : "bg-white text-gray-700 border-gray-300"
              }`}
              onClick={() => setWorkType(type.value)}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-8">
        <label className="text-left mb-4 text-xl font-bold">{t.willingToRelocate}</label>
        <div className="flex space-x-2">
          {[
            { value: "Yes", label: t.yes },
            { value: "No", label: t.no },
            { value: "Maybe", label: t.maybe }
          ].map((option) => (
            <button
              key={option.value}
              className={`px-6 py-2 rounded-full border ${
                relocation === option.value
                  ? "bg-buttonColor text-white"
                  : "bg-white text-gray-700 border-gray-300"
              }`}
              onClick={() => setRelocation(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

     
      </div>
      <div className="flex gap-2 mt-4 justify-end">
          <button
            className="py-2 px-4 rounded-md border"
            onClick={HandleBack}
          >
            {t.back}
          </button>
          <button
            className={`py-2 px-4 rounded-md bg-buttonColor text-white border ${!isCareerFormValid ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleSubmit}
            disabled={!isCareerFormValid}
          >
            {t.next}
          </button>
        </div>
    </div>
  );
} 